import { useState, useMemo, useEffect } from "react";

function formatPrecio(n) {
  return "$" + n.toLocaleString("es-CL");
}

export default function Aranceles() {
  const [aranceles, setAranceles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedCats, setExpandedCats] = useState({});

  useEffect(() => {
    const fetchAranceles = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://ingresosolicitudes.dentistacontralor.cl/api/v1/public/categories"
        );
        if (!response.ok) throw new Error("Error al cargar los aranceles");

        const json = await response.json();
        const categories = json.data || json;

        // Transformar la estructura del API para compatibilidad
        const transformedData = categories.map((cat) => ({
          id: cat.id,
          name: cat.nombre,
          categoria: cat.nombre,
          items: (cat.tratamientos || []).map((item) => ({
            id: item.id,
            nombre: item.name,
            name: item.name,
            precio: item.price,
            price: item.price,
            description: item.description,
          })),
          treatments: cat.tratamientos || [],
        }));

        setAranceles(transformedData);

        // Expandir todas las categorías por defecto
        const initialExpanded = Object.fromEntries(
          transformedData.map((c) => [c.id, true])
        );
        setExpandedCats(initialExpanded);
        setError(null);
      } catch (err) {
        setError(err.message);
        setAranceles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAranceles();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return aranceles;
    return aranceles
      .map((cat) => {
        const items = (cat.items || cat.treatments || []).filter((item) => {
          const itemName = item.nombre || item.name || "";
          return itemName.toLowerCase().includes(q);
        });
        return {
          ...cat,
          items,
        };
      })
      .filter((cat) => cat.items.length > 0);
  }, [search, aranceles]);

  const toggleCat = (catKey) =>
    setExpandedCats((prev) => ({ ...prev, [catKey]: !prev[catKey] }));

  if (loading) {
    return (
      <section className="px-6 md:px-8 py-8 max-w-5xl mx-auto">
        <div className="section-tag">Aranceles y Protocolos</div>
        <h2 className="section-title">Aranceles de Referencia</h2>
        <div className="text-center py-12 text-gray-400 text-sm">
          Cargando aranceles...
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="px-6 md:px-8 py-8 max-w-5xl mx-auto">
        <div className="section-tag">Aranceles y Protocolos</div>
        <h2 className="section-title">Aranceles de Referencia</h2>
        <div className="text-center py-12 text-red-500 text-sm">
          Error al cargar los aranceles: {error}
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 md:px-8 py-8 max-w-5xl mx-auto">
      <div className="section-tag">Aranceles y Protocolos</div>
      <h2 className="section-title">Aranceles de Referencia</h2>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 mb-5 shadow-sm">
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" className="text-gray-400 flex-shrink-0">
          <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M10.5 10.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar procedimiento…"
          className="flex-1 text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="text-gray-400 hover:text-gray-600 text-xs"
          >
            ✕
          </button>
        )}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">
          No se encontraron procedimientos para "{search}"
        </div>
      )}

      <div className="flex flex-col gap-3">
        {filtered.map((cat) => {
          const catKey = cat.id || cat.name;
          const catName = cat.name || cat.categoria || "Sin categoría";
          const items = cat.items || cat.treatments || [];
          return (
            <div key={catKey} className="card overflow-hidden">
              {/* Category header */}
              <button
                onClick={() => toggleCat(catKey)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors"
              >
                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  {catName}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-gray-400">
                    {items.length} procedimiento{items.length !== 1 ? "s" : ""}
                  </span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className={`text-gray-400 transition-transform ${
                      expandedCats[catKey] ? "rotate-180" : ""
                    }`}
                  >
                    <path
                      d="M3 5l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </button>

              {/* Items */}
              {expandedCats[catKey] && (
                <div>
                  {items.map((item, i) => {
                    const itemName = item.nombre || item.name || "Sin nombre";
                    const itemPrice = item.precio || item.price || 0;
                    return (
                      <div
                        key={i}
                        className={`flex items-center justify-between px-4 py-2.5 ${
                          i < items.length - 1 ? "border-b border-gray-100" : ""
                        } hover:bg-gray-50/50 transition-colors`}
                      >
                        <span className="text-sm text-gray-700">{itemName}</span>
                        <span className="text-sm font-semibold text-navy-600 bg-gray-100 px-3 py-1 rounded-md tabular-nums ml-4 flex-shrink-0">
                          {formatPrecio(itemPrice)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 mt-5 text-center">
        Los valores corresponden al arancel de referencia del convenio. El reembolso es del 85% del valor aprobado.
      </p>
    </section>
  );
}
